
import {
  Box,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  Switch,
  Divider,
  IconButton,
} from '@chakra-ui/react'
import { X } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { PageBlock } from '@/types/pageBuilder'

interface BlockSettingsProps {
  block: PageBlock | null
  onUpdate: (content: Record<string, any>) => void
  onClose: () => void
}

export function BlockSettings({ block, onUpdate, onClose }: BlockSettingsProps) {
  const colors = useThemeColors()

  if (!block) {
    return (
      <Box
        w="320px"
        h="100%"
        borderLeft="1px"
        borderColor={colors.border}
        bg={colors.cardBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Text color={colors.textMuted} textAlign="center" fontSize="sm">
          Select a block to edit its settings
        </Text>
      </Box>
    )
  }

  const renderSettings = () => {
    switch (block.type) {
      case 'heading':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Heading Text</FormLabel>
              <Input
                value={block.content.text || ''}
                onChange={(e) => onUpdate({ ...block.content, text: e.target.value })}
                placeholder="Enter heading text"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Heading Level</FormLabel>
              <Select
                value={block.content.level || 2}
                onChange={(e) => onUpdate({ ...block.content, level: parseInt(e.target.value) })}
                size="sm"
              >
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
                <option value={4}>H4</option>
                <option value={5}>H5</option>
                <option value={6}>H6</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'left'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'text':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Text Content</FormLabel>
              <Textarea
                value={block.content.text || ''}
                onChange={(e) => onUpdate({ ...block.content, text: e.target.value })}
                placeholder="Enter text content"
                rows={6}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'left'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'image':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Image URL</FormLabel>
              <Input
                value={block.content.url || ''}
                onChange={(e) => onUpdate({ ...block.content, url: e.target.value })}
                placeholder="https://example.com/image.jpg"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alt Text</FormLabel>
              <Input
                value={block.content.alt || ''}
                onChange={(e) => onUpdate({ ...block.content, alt: e.target.value })}
                placeholder="Image description"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Caption</FormLabel>
              <Input
                value={block.content.caption || ''}
                onChange={(e) => onUpdate({ ...block.content, caption: e.target.value })}
                placeholder="Image caption (optional)"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'center'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Width</FormLabel>
              <Input
                value={block.content.width || '100%'}
                onChange={(e) => onUpdate({ ...block.content, width: e.target.value })}
                placeholder="100% or 500px"
                size="sm"
              />
            </FormControl>
          </VStack>
        )

      case 'video':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Video URL</FormLabel>
              <Input
                value={block.content.url || ''}
                onChange={(e) => onUpdate({ ...block.content, url: e.target.value })}
                placeholder="https://example.com/video.mp4"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Caption</FormLabel>
              <Input
                value={block.content.caption || ''}
                onChange={(e) => onUpdate({ ...block.content, caption: e.target.value })}
                placeholder="Video caption (optional)"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'center'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel fontSize="sm" mb={0}>Autoplay</FormLabel>
              <Switch
                isChecked={block.content.autoplay || false}
                onChange={(e) => onUpdate({ ...block.content, autoplay: e.target.checked })}
                size="sm"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel fontSize="sm" mb={0}>Show Controls</FormLabel>
              <Switch
                isChecked={block.content.controls !== false}
                onChange={(e) => onUpdate({ ...block.content, controls: e.target.checked })}
                size="sm"
              />
            </FormControl>
          </VStack>
        )

      case 'button':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Button Text</FormLabel>
              <Input
                value={block.content.text || ''}
                onChange={(e) => onUpdate({ ...block.content, text: e.target.value })}
                placeholder="Click me"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Button URL</FormLabel>
              <Input
                value={block.content.url || ''}
                onChange={(e) => onUpdate({ ...block.content, url: e.target.value })}
                placeholder="https://example.com"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Button Style</FormLabel>
              <Select
                value={block.content.style || 'primary'}
                onChange={(e) => onUpdate({ ...block.content, style: e.target.value })}
                size="sm"
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="outline">Outline</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'left'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'quote':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Quote Text</FormLabel>
              <Textarea
                value={block.content.text || ''}
                onChange={(e) => onUpdate({ ...block.content, text: e.target.value })}
                placeholder="Enter quote text"
                rows={4}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Author</FormLabel>
              <Input
                value={block.content.author || ''}
                onChange={(e) => onUpdate({ ...block.content, author: e.target.value })}
                placeholder="Quote author (optional)"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'left'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'list':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">List Type</FormLabel>
              <Select
                value={block.content.type || 'ul'}
                onChange={(e) => onUpdate({ ...block.content, type: e.target.value })}
                size="sm"
              >
                <option value="ul">Unordered List</option>
                <option value="ol">Ordered List</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">List Items (one per line)</FormLabel>
              <Textarea
                value={Array.isArray(block.content.items) ? block.content.items.join('\n') : ''}
                onChange={(e) => onUpdate({ 
                  ...block.content, 
                  items: e.target.value.split('\n').filter(item => item.trim()) 
                })}
                placeholder="Item 1&#10;Item 2&#10;Item 3"
                rows={6}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Alignment</FormLabel>
              <Select
                value={block.content.alignment || 'left'}
                onChange={(e) => onUpdate({ ...block.content, alignment: e.target.value })}
                size="sm"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'spacer':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Height (px)</FormLabel>
              <NumberInput
                value={block.content.height || 40}
                onChange={(_, value) => onUpdate({ ...block.content, height: isNaN(value) ? 40 : value })}
                min={10}
                max={500}
                size="sm"
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </VStack>
        )

      case 'divider':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Divider Style</FormLabel>
              <Select
                value={block.content.style || 'solid'}
                onChange={(e) => onUpdate({ ...block.content, style: e.target.value })}
                size="sm"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'code':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Code</FormLabel>
              <Textarea
                value={block.content.code || ''}
                onChange={(e) => onUpdate({ ...block.content, code: e.target.value })}
                placeholder="Enter code"
                rows={8}
                size="sm"
                fontFamily="mono"
                fontSize="xs"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Language</FormLabel>
              <Select
                value={block.content.language || 'javascript'}
                onChange={(e) => onUpdate({ ...block.content, language: e.target.value })}
                size="sm"
              >
                <option value="javascript">JavaScript</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="php">PHP</option>
                <option value="python">Python</option>
                <option value="json">JSON</option>
              </Select>
            </FormControl>
          </VStack>
        )

      case 'html':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">HTML Code</FormLabel>
              <Textarea
                value={block.content.html || ''}
                onChange={(e) => onUpdate({ ...block.content, html: e.target.value })}
                placeholder="Enter HTML code"
                rows={8}
                size="sm"
                fontFamily="mono"
                fontSize="xs"
              />
            </FormControl>
          </VStack>
        )

      case 'columns':
        return (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Number of Columns</FormLabel>
              <Select
                value={block.content.columns || 2}
                onChange={(e) => {
                  const newColumnCount = parseInt(e.target.value)
                  const currentColumnBlocks = block.content.columnBlocks || []
                  // Adjust columnBlocks array to match new column count
                  let updatedColumnBlocks = [...currentColumnBlocks]
                  if (newColumnCount > currentColumnBlocks.length) {
                    // Add empty arrays for new columns
                    while (updatedColumnBlocks.length < newColumnCount) {
                      updatedColumnBlocks.push([])
                    }
                  } else if (newColumnCount < currentColumnBlocks.length) {
                    // Remove excess columns (keep their content for now, just truncate)
                    updatedColumnBlocks = updatedColumnBlocks.slice(0, newColumnCount)
                  }
                  onUpdate({ 
                    ...block.content, 
                    columns: newColumnCount,
                    columnBlocks: updatedColumnBlocks,
                  })
                }}
                size="sm"
              >
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Gap</FormLabel>
              <Select
                value={block.content.gap || 'medium'}
                onChange={(e) => onUpdate({ ...block.content, gap: e.target.value })}
                size="sm"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </Select>
            </FormControl>
            <Text fontSize="xs" color={colors.textMuted}>
              Click "Add Block" inside each column to add content. Select blocks in columns to edit them.
            </Text>
          </VStack>
        )

      default:
        return <Text fontSize="sm" color={colors.textMuted}>No settings available for this block type.</Text>
    }
  }

  return (
    <Box
      w="100%"
      h="100%"
      bg={colors.cardBg}
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Box
        p={3}
        borderBottom="1px"
        borderColor={colors.border}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text fontSize="sm" fontWeight="semibold" color={colors.text}>
          Block Settings
        </Text>
        <IconButton
          aria-label="Close settings"
          icon={<X size={16} />}
          size="xs"
          variant="ghost"
          onClick={onClose}
        />
      </Box>

      {/* Settings Content */}
      <Box flex="1" overflowY="auto" p={4}>
        <VStack spacing={4} align="stretch">
          <Box>
            <Text fontSize="xs" color={colors.textMuted} mb={2} textTransform="uppercase" fontWeight="semibold">
              {block.type.charAt(0).toUpperCase() + block.type.slice(1)} Block
            </Text>
            <Divider />
          </Box>
          {renderSettings()}
        </VStack>
      </Box>
    </Box>
  )
}

